// src/services/SessionApiService.jsx - Session-based Multi-Tenant API Service

import { apiErrorFrom, NetworkError } from '../utils/parseApiError';
import { getSessionId } from '../utils/sessionId';

// The user-context cache is keyed by SHAPE, not just by name. It holds a
// server payload for up to 5 minutes in sessionStorage, so a deploy that adds a
// field to that payload would otherwise be read back without it by every tab
// open across the deploy — and code that (correctly) refuses to guess a missing
// field then throws. Bump this whenever the /auth/user-context/ payload gains
// or renames a field; old entries are simply never read again.
const USER_CACHE_KEY = 'userCache:v3';
const USER_CACHE_TIME_KEY = 'userCacheTime:v3';

/**
 * Extract tenant slug from current hostname
 */
export function extractTenantSlug() {
  const hostname = window.location.hostname;

  // Development domains
  if (hostname.endsWith('.localhost')) {
    if (!['api.localhost', 'www.localhost', 'localhost'].includes(hostname)) {
      return hostname.split('.')[0];
    }
  }

  // Production domains
  if (hostname.endsWith('.manexys.com')) {
    // Exclude special subdomains (not tenant subdomains)
    const excludedSubdomains = ['api.manexys.com', 'www.manexys.com', 'app.manexys.com'];
    if (!excludedSubdomains.includes(hostname)) {
      return hostname.split('.')[0];
    }
  }

  return null;
}

/**
 * Get API base URL - Works with Vite proxy in development
 */
export function getApiBaseUrl() {
  // In development, use Vite proxy (no need for full URL)
  if (import.meta.env.DEV) {
    return ''; // Empty string uses current origin with Vite proxy
  }

  // Environment variable override (highest priority in production)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Production: Always use centralized API server
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  if (hostname.includes('manexys.com')) {
    return `${protocol}//api.manexys.com`;
  }

  // A production build on any other host has no API to talk to unless
  // VITE_API_URL names one.
  throw new Error(
    `No API base URL for host ${hostname}: set VITE_API_URL for this deployment.`,
  );
}


/**
 * CSRF token for a mutating request.
 *
 * Django keeps the canonical token in the `csrftoken` cookie
 * (backend/backend/deployment.py: CSRF_COOKIE_HTTPONLY = False, so the page
 * may read it). It is read fresh for EVERY mutation — nothing is cached, so a
 * token the server rotates (login does) is what the next request carries;
 * there is no retry-after-403 path and must not be one (a retry re-sends the
 * mutation the server just refused). When the cookie is not readable — a
 * cross-site first load before the server has set it — GET /auth/csrf-token/
 * sets it and returns it; concurrent callers share the one in-flight fetch.
 * A failed fetch throws: a mutation sent without a token is refused by the
 * server anyway, and sending it would only turn one error into a vaguer one.
 */
let csrfTokenFetch = null;

function csrfTokenFromCookie() {
  for (const cookie of document.cookie.split(';')) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }
  return null;
}

async function fetchCsrfToken() {
  const response = await fetch(`${getApiBaseUrl()}/auth/csrf-token/`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`CSRF token request failed: HTTP ${response.status}`);
  }
  const body = await response.json();
  if (!body.csrfToken) {
    throw new Error('CSRF token response carried no token');
  }
  return body.csrfToken;
}

async function csrfToken() {
  const cookieToken = csrfTokenFromCookie();
  if (cookieToken) {
    return cookieToken;
  }
  if (!csrfTokenFetch) {
    csrfTokenFetch = fetchCsrfToken().finally(() => { csrfTokenFetch = null; });
  }
  return csrfTokenFetch;
}

/**
 * Read the selected store id for the X-Store-ID header.
 *
 * Returns null when no store is selected. That is a legitimate state — the
 * login, registration and password-reset calls all run through apiRequest
 * before any store exists, as do org-level endpoints — and the backend
 * resolves scope from `request.session['selected_store_id']` in that case
 * (backend/core/store_context.py, "Priority 3").
 *
 * THROWS when localStorage itself is unreachable, which is a different thing
 * entirely. Swallowing that and omitting the header would send an unscoped
 * request, and the backend's session fallback would quietly serve whichever
 * store the server session last selected — potentially not the one the UI is
 * showing. Store scope is not something to guess at, so the request fails
 * instead of going out wrong.
 */
function readSelectedStoreId() {
  try {
    return localStorage.getItem('selectedStoreId');
  } catch (err) {
    // Thrown before apiRequest's own try/catch, so log here to guarantee the
    // cause is visible rather than only surfacing as a failed request.
    console.error(
      'SessionApiService: cannot read selectedStoreId — browser storage is '
      + 'unavailable, so the request cannot be store-scoped and was not sent.',
      err
    );
    const error = new Error(
      'Cannot determine the selected store: browser storage is unavailable.',
      { cause: err }
    );
    error.code = 'store_context_unavailable';
    throw error;
  }
}

/**
 * Enhanced fetch wrapper for session-based authentication
 */
/**
 * Leaves the one-shot notice the login page shows on its next mount
 * (components/login/Login.jsx consumes both keys).
 * @param {'expired' | 'forbidden'} reason
 */
export function markSessionExpired(reason) {
  sessionStorage.setItem('sessionExpired', 'true');
  sessionStorage.setItem('sessionExpiredReason', reason);
}

export async function apiRequest(endpoint, options = {}) {
  const baseUrl = getApiBaseUrl();
  const tenantSlug = extractTenantSlug();
  const url = `${baseUrl}${endpoint}`;

  // Default headers - skip Content-Type for FormData (let browser set multipart boundary)
  const isFormData = options.body instanceof FormData;
  const defaultHeaders = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
  };

  // Add tenant context headers
  if (tenantSlug) {
    defaultHeaders['X-Tenant-Slug'] = tenantSlug;
    defaultHeaders['X-Tenant-Domain'] = window.location.hostname;
  }

  // Add store context header for store-scoped API filtering.
  // Store ID is saved to localStorage when user selects a store or via SSO.
  // No selection is fine (pre-login / org-level calls); an unreadable store
  // selection is not — readSelectedStoreId throws rather than let the request
  // go out unscoped. See its docstring.
  const selectedStoreId = readSelectedStoreId();
  if (selectedStoreId) {
    defaultHeaders['X-Store-ID'] = selectedStoreId;
  }

  // Add session ID for analytics tracking. getSessionId owns the storage
  // access and never throws — an optional analytics header must not be able
  // to fail the request it rides on.
  defaultHeaders['X-Session-ID'] = getSessionId();

  // Add CSRF token for mutating requests
  const method = options.method || 'GET';
  const isMutatingRequest = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());

  if (isMutatingRequest) {
    defaultHeaders['X-CSRFToken'] = await csrfToken();
  }

  // Merge headers
  const headers = {
    ...defaultHeaders,
    ...(options.headers || {}),
  };

  // Default options for session-based auth
  const defaultOptions = {
    credentials: 'include',  // CRITICAL: Include cookies
    headers,
    mode: 'cors',
  };

  // Merge options
  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers, // Ensure headers are properly merged
  };

  let response;
  try {
    response = await fetch(url, finalOptions);
  } catch (error) {
    // Nothing came back. AbortError is intentional cancellation (the caller
    // used AbortController), not a failure; anything else means the request
    // never reached the server, which is the one failure worth retrying.
    if (error.name === 'AbortError') throw error;
    console.error('Session API Error:', error);
    throw new NetworkError(`${method.toUpperCase()} ${url}`, error);
  }

  // Session expiration (401/403). A CSRF rejection is a 403 too, but not a
  // dead session: the response goes back to the caller as the error it is
  // (see csrfToken above for why it is never retried).
  if (response.status === 401 || response.status === 403) {
    // clone() preserves the original body for callers to parse
    const errorText = await response.clone().text();

    if (errorText.includes('CSRF')) {
      console.error(`CSRF rejection on ${method.toUpperCase()} ${url}: ${errorText}`);
    } else if (sessionStorage.getItem(USER_CACHE_KEY)) {
      // userCache exists → user previously had an active session. A 401/403
      // now means the server session died (expired, terminated, or cookie lost).
      // Without userCache (e.g., visiting /login for the first time), 401/403
      // is expected and should not trigger session-expired UI.
      sessionStorage.removeItem(USER_CACHE_KEY);
      sessionStorage.removeItem(USER_CACHE_TIME_KEY);

      // The store selection is session-scoped — drop it so a stale
      // X-Store-ID can't ride a later request before the next sign-in's
      // user-context resync re-establishes the authoritative store.
      localStorage.removeItem('selectedStoreId');

      markSessionExpired(response.status === 401 ? 'expired' : 'forbidden');
    }
  }

  return response;
}

/**
 * Convenience methods for common HTTP verbs with session support
 */
export const sessionApi = {
  get: (endpoint, options = {}) =>
    apiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'DELETE',
      ...(data ? { body: JSON.stringify(data) } : {}),
    }),
};

/**
 * Session-specific API endpoints
 */
export const sessionService = {
  /**
   * Initialize CSRF protection
   */
  initCSRF: async () => {
    const response = await sessionApi.get('/auth/csrf-token/');
    if (!response.ok) throw await apiErrorFrom(response, 'GET /auth/csrf-token/');
    return response.json();
  },

  /**
   * Login with tenant context
   */
  login: async (credentials) => {
    const response = await sessionApi.post('/auth/login/', credentials);

    // Throws like every other refusal in this service: the status is what the
    // sign-in page branches on (401 wrong password, 429 rate limited).
    if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/login/');

    const data = await response.json();

    // Past the refusal above this IS the successful login, so the previous
    // user's cached context goes unconditionally — a `data.success` gate here
    // would leave it for whoever signs in next on a shared machine.
    sessionStorage.removeItem(USER_CACHE_KEY);
    sessionStorage.removeItem(USER_CACHE_TIME_KEY);

    return data;
  },

  /**
   * Logout from current tenant
   */
  logout: async () => {
    const response = await sessionApi.post('/auth/logout/', {});
    const data = await response.json();

    // Clear any cached user data
    sessionStorage.removeItem(USER_CACHE_KEY);
    sessionStorage.removeItem(USER_CACHE_TIME_KEY);

    return data;
  },

  /**
   * Lightweight auth probe — always 200, returns {authenticated, user_id}.
   * Anonymous, it also carries `shopify_sign_in_url`: the door for the shop
   * this browser last signed in from (or null), which a protected route
   * follows instead of showing the login page (App.jsx ProtectedLayout).
   * Use this before getUserContext() so anonymous visitors don't produce
   * `Unauthorized: /auth/user-context/` warnings in the backend logs.
   */
  whoami: async () => {
    const response = await sessionApi.get('/auth/whoami/');
    if (!response.ok) throw await apiErrorFrom(response, 'GET /auth/whoami/');
    return response.json();
  },

  /**
   * Get current user context with tenant information. Throws on a non-OK
   * response: parsing an error body here would let `{detail: …}` flow into
   * applyUserContext as a "user context" — and isAuthenticated is derived
   * from that object's existence.
   */
  getUserContext: async () => {
    const response = await sessionApi.get('/auth/user-context/');
    if (!response.ok) throw await apiErrorFrom(response, 'GET /auth/user-context/');
    return response.json();
  },

  /**
   * Get current user profile
   */
  getUserProfile: async () => {
    const response = await sessionApi.get('/auth/user-profile/me/');
    if (!response.ok) throw new Error('Failed to load user profile');
    return response.json();
  },

  /**
   * Update current user profile (partial update)
   */
  updateUserProfile: async (data) => {
    const response = await sessionApi.patch('/auth/user-profile/me/', data);
    // A submitted `email` starts a verified change, and its refusals are
    // typed (`email_unchanged`, `pending_email_taken` — DomainError in
    // authentication/views/user_profile.py).
    if (!response.ok) throw await apiErrorFrom(response);
    return response.json();
  },

  /**
   * The profile's "Email me a link" for a person without a password:
   * POST /auth/setup-link/ mails the one-time setup link. Refusals carry a
   * code (`has_password`, `email_unverified`, `setup_link_rate_limited`)
   * the caller translates via `apiErrors.<code>`.
   */
  requestSetupLink: async () => {
    const response = await sessionApi.post('/auth/setup-link/', {});
    if (!response.ok) throw await apiErrorFrom(response);
    return response.json();
  },

  /**
   * Change password
   */
  changePassword: async (currentPassword, newPassword) => {
    const response = await sessionApi.post('/auth/user-profile/change-password/', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: newPassword,
    });
    if (!response.ok) throw await apiErrorFrom(response);
    return response.json();
  },

  /**
   * Upload user avatar
   */
  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await apiRequest('/auth/user-profile/avatar/', { method: 'POST', body: formData });

    if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/user-profile/avatar/');

    return response.json();
  },

  /**
   * Delete user avatar
   */
  deleteAvatar: async () => {
    const response = await sessionApi.delete('/auth/user-profile/avatar/');

    if (!response.ok) throw await apiErrorFrom(response, 'DELETE /auth/user-profile/avatar/');

    return response.json();
  },

  /**
   * Upload the current store's logo (printed on its PO PDFs)
   */
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiRequest('/auth/stores/logo/', { method: 'POST', body: formData });

    if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/stores/logo/');

    return response.json();
  },

  /**
   * Delete the current store's logo
   */
  deleteLogo: async () => {
    const response = await sessionApi.delete('/auth/stores/logo/');
    if (!response.ok) throw await apiErrorFrom(response, 'DELETE /auth/stores/logo/');
    return response.json();
  },

  /**
   * Get list of stores for current user's tenant
   */
  getStores: async () => {
    const response = await sessionApi.get('/auth/stores/');
    if (!response.ok) throw new Error('Failed to load stores');
    return response.json();
  },

  /**
   * Get current store + organization settings
   */
  getStoreSettings: async () => {
    const response = await sessionApi.get('/auth/stores/settings/');
    if (!response.ok) throw new Error('Failed to load store settings');
    return response.json();
  },

  /**
   * Update current store + organization settings.
   *
   * A 400 from this endpoint is per-field: `_put_settings` collects
   * `{errors: {'<section>.<field>': '<message>'}}` (authentication/views/
   * stores.py) rather than failing on the first bad value. `err.fieldErrors`
   * carries that map through so the caller can render each message on the
   * control that produced it — a merchant who typed 400 days of supply must
   * see it on that field, not as "Failed to save settings" over a card whose
   * other five values were fine. `err.message` stays dev-facing.
   */
  updateStoreSettings: async (data) => {
    const response = await sessionApi.patch('/auth/stores/settings/', data);
    if (!response.ok) {
      const err = await apiErrorFrom(response);
      if (err.body?.errors && typeof err.body.errors === 'object') {
        err.fieldErrors = err.body.errors;
      }
      throw err;
    }
    return response.json();
  },

  /**
   * Poll the current store's product category source + in-flight switch state.
   */
  getCategorySource: async () => {
    const response = await sessionApi.get('/auth/stores/category-source/');
    if (!response.ok) throw new Error('Failed to load category source');
    return response.json();
  },

  /**
   * Queue a switch of the current store's product category source. The rebuild
   * runs on the pipeline rail; the returned state carries switch_in_progress.
   */
  switchCategorySource: async (categorySource) => {
    const response = await sessionApi.post('/auth/stores/category-source/', {
      category_source: categorySource,
    });
    if (!response.ok) throw await apiErrorFrom(response);
    return response.json();
  },

  /**
   * Select a store for the current session
   * Updates both backend session and localStorage
   */
  selectStore: async (storeId) => {
    const response = await sessionApi.post('/auth/stores/select/', { store_id: storeId });

    if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/stores/select/');

    const data = await response.json();

    // Update localStorage with new store ID for API header
    if (data.selected_store?.id) {
      localStorage.setItem('selectedStoreId', data.selected_store.id);
    }

    return data;
  },

};

/**
 * Authentication state management
 */
export const authState = {
  /**
   * Get cached user data (with fallback to server)
   */
  getCurrentUser: async () => {
    try {
      // Try cache first (for performance)
      const cached = sessionStorage.getItem(USER_CACHE_KEY);
      const cacheTimestamp = sessionStorage.getItem(USER_CACHE_TIME_KEY);

      // Use cache if less than 5 minutes old
      if (cached && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < 5 * 60 * 1000) { // 5 minutes - balances performance vs freshness
          return JSON.parse(cached);
        }
      }

      // Fetch from server
      const data = await sessionService.getUserContext();

      // Cache the result
      sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(data));
      sessionStorage.setItem(USER_CACHE_TIME_KEY, Date.now().toString());

      return data;
    } catch (error) {
      // Clear invalid cache
      sessionStorage.removeItem(USER_CACHE_KEY);
      sessionStorage.removeItem(USER_CACHE_TIME_KEY);
      throw error;
    }
  },

  /**
   * Clear cached authentication data.
   * NOTE: selectedStoreId is user state, not cache — only cleared on logout.
   */
  clearCache: () => {
    sessionStorage.removeItem(USER_CACHE_KEY);
    sessionStorage.removeItem(USER_CACHE_TIME_KEY);
  },

  /**
   * Full cleanup on logout — clears cache AND user state (store selection).
   */
  clearAll: () => {
    sessionStorage.removeItem(USER_CACHE_KEY);
    sessionStorage.removeItem(USER_CACHE_TIME_KEY);
    localStorage.removeItem('selectedStoreId');
  },
};
